<script lang="ts">
/**
 * ErrorBoundary — catches errors thrown by descendant components.
 *
 * Vue propagates child errors up through `errorCaptured`. Returning
 * `false` from the hook stops propagation, so we can render a
 * fallback while keeping the rest of the app alive.
 *
 *   <ErrorBoundary>
 *     <SomeFlakyContent />
 *   </ErrorBoundary>
 *
 * Without this wrapper, a single throw in a page-level component
 * crashes the entire RouterView and the user sees a white screen.
 * With it, the user sees a calm ErrorState card + a "Try again"
 * button. The boundary stays mounted; resetting clears the error
 * and re-renders the slot.
 *
 * Customization:
 *   <ErrorBoundary>
 *     <template #fallback="{ error, reset }">
 *       <MyCustomErrorCard :error="error" @retry="reset" />
 *     </template>
 *     <DefaultContent />
 *   </ErrorBoundary>
 *
 * Used at three levels in this app:
 *   - Each Layout wraps its RouterView
 *   - Pages can opt into a narrower boundary around a flaky card
 *
 * Note: Options API used so the `errorCaptured` hook reads
 * naturally. The Composition equivalent (`onErrorCaptured`) is
 * also fine but requires more plumbing for the slot fallback.
 */
import { defineComponent, h } from 'vue'
import i18n from '@/i18n'
import ErrorState from './ErrorState.vue'

export default defineComponent({
  name: 'ErrorBoundary',

  emits: ['error', 'reset'],

  data() {
    return {
      capturedError: null as Error | null,
      capturedInfo: '',
    }
  },

  errorCaptured(err: unknown, instance: unknown, info: string) {
    void instance
    this.capturedError = err as Error
    this.capturedInfo = info
    this.$emit('error', { err, info, instance })
    /* Stop propagation — we render the fallback. */
    return false
  },

  methods: {
    reset() {
      this.capturedError = null
      this.capturedInfo = ''
      this.$emit('reset')
    },
  },

  render() {
    if (this.capturedError) {
      if (this.$slots.fallback) {
        return this.$slots.fallback({
          error: this.capturedError,
          info: this.capturedInfo,
          reset: this.reset,
        })
      }
      /* The global composer: this wraps every layout, including the ones a
       * test mounts without the i18n plugin. */
      const { t } = i18n.global
      return h(ErrorState, {
        title: t('states.errorTitle'),
        message: this.capturedError?.message || t('ui.error.render'),
        onRetry: this.reset,
      })
    }
    return this.$slots.default ? this.$slots.default() : null
  },
})
</script>
